import Button from '@/components/Button';
import Input from '@/components/Input';
import ModalV2 from '@/components/ModalV2';
import Panel from '@/components/panel/Panel';
import PopoverInput from '@/components/PopoverInput';
import { FiSearch, MdOutlineDashboardCustomize } from '@/components/react-icons/main';
import Select from '@/components/Select';
import Tag from '@/components/Tag';
import TextWithEllipsis from '@/components/TextWithEllipsis.jsx';
import FloatingSelectV2 from '@/components/v2/FloatingSelectV2';
import IconV2 from '@/components/v2/IconV2';
import clsx from '@/lib/clsx';
import confirmDialog from '@/lib/confirmDialog';
import { AddIcon, DeleteIcon } from '@/lib/constants/icons';
import getValue from '@/lib/getValue';
import { setModalState } from '@/redux/actions/uiActions/modalActions';
import memoizeSelector from '@/redux/lib/memoizeSelector';
import { modal } from '@/redux/selectors/uiSelectors';
import DataTypeIcon from '@/screens/apps/components/DataTypeIcon';
import mockTypeActions from '@/screens/apps/redux/actions/mockTypeActions';
import { getActionModelSelectOptions } from '@shared/schema/actionModels';
import appSchema from '@shared/schema/appSchema';
import { generateObjectSlug } from '@shared/utils/generateObjectSlug';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import MockDataTypeEntries from './mockDataTypeModal/MockDataTypeEntries';

const dataTypeOptions = appSchema.general.dataTypes.map((n) => ({
  label: n.label,
  value: n.value,
  icon: <DataTypeIcon label={n.label} />
}));

const MockDataTypesModal = () => {
  const mockDataTypes = useSelector((state) => state?.settings?.mockDataTypes);
  const modalState = useSelector(memoizeSelector('modal:mockDataTypes', (state) => modal(state, 'mockDataTypes')));
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const showRef = useRef(false);

  const sortedMockDataTypes = useMemo(() => mockDataTypes?.sort((a, b) => new Date(b?.createdOn) - new Date(a?.createdOn))?.filter((n) => n?.name?.toLowerCase()?.includes(search?.toLowerCase())), [mockDataTypes, search]);
  const selected = useMemo(() => mockDataTypes?.find((mockDataType) => mockDataType?.id === selectedId), [mockDataTypes, selectedId]);
  const modelSelectOptions = useMemo(() => getActionModelSelectOptions({ selectedModel: selected?.model }), [selected?.model]);
  const show = modalState?.open;
  const initialSelectedId = modalState?.initialSelectedId;

  const handleClose = () => {
    setModalState('mockDataTypes', { open: false });
  };

  const handleAdd = async () => {
    const mockType = await mockTypeActions.create();
    setSelectedId(mockType?.id);
  };

  const handleDataTypeChange = ({ value }) => {
    mockTypeActions.set('dataType', value, { mockTypeId: selectedId });
  };

  const handleModelChange = ({ value }) => {
    mockTypeActions.set('model', value, { mockTypeId: selectedId });
  };

  const handleNameSave = (value) => {
    mockTypeActions.set('name', value, { mockTypeId: selectedId });
    mockTypeActions.set('key', generateObjectSlug(value), { mockTypeId: selectedId });
  };

  const handleDelete = async () => {
    const confirmed = await confirmDialog({
      title: 'Delete Mock Type',
      message: 'Are you sure you want to delete this mock type? This action cannot be undone.',
      confirm: 'Delete'
    });

    if (confirmed === true) {
      mockTypeActions.delete(selectedId);
    }
  };

  const actions = [{ children: 'Done', onClick: handleClose }];

  const renderIcon = useCallback((mockDataType) => {
    return <DataTypeIcon className={`id-${mockDataType?.id}`} label={mockDataType?.dataType} />;
  }, []);

  useEffect(() => {
    if (show !== showRef.current) {
      setSelectedId(show ? initialSelectedId || mockDataTypes?.[0]?.id : null);
    }

    showRef.current = show;
  }, [show, initialSelectedId]);

  return (
    <ModalV2 //
      maxWidth={'800px'}
      className={'mock-data-types-modal'}
      show={show}
      onHide={handleClose}
      actions={actions}
      centered
      noHeader>
      <div className="first-column">
        <Input icon={FiSearch} placeholder="Search" value={search} onChange={(e) => setSearch(e.target.value)} autoFocus />
        <div className="group-header">
          <div className="group-label">Mock Types</div>
          {!search && (
            <Button className="dashed" size="xs" icon={AddIcon} onClick={handleAdd}>
              Add
            </Button>
          )}
        </div>
        {sortedMockDataTypes?.length === 0 && <Panel.EmptyState title={'No mock data types found'} description={'Create a mock data type to get started.'} icon={MdOutlineDashboardCustomize} />}
        {sortedMockDataTypes?.length > 0 && (
          <div className="mock-data-types-list">
            {sortedMockDataTypes?.map((mockDataType, idx) => (
              <div key={idx} className={clsx('mock-data-type', selectedId === mockDataType?.id && 'selected')} onClick={() => setSelectedId(mockDataType?.id)}>
                <IconV2 icon={renderIcon(mockDataType)} />
                <TextWithEllipsis className="mock-data-type-name">{mockDataType?.name}</TextWithEllipsis>
                <Tag className="count-tag" data-title={`Used in ${mockDataType?.count} attribute${+mockDataType?.count === 1 ? '' : 's'}`}>
                  {mockDataType?.count || 0}
                </Tag>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="second-column">
        {selected && (
          <>
            <div className="header-row">
              <div className="left">
                <IconV2 icon={renderIcon(selected)} className="data-type-icon" />
                <FloatingSelectV2 triggers={['.data-type-icon']} options={dataTypeOptions} useSearch={false} useNumbers onChange={handleDataTypeChange} />
                <PopoverInput initialValue={selected?.name} onSave={handleNameSave} width={240} side="bottom" align="start" className="name" title={'Name'}>
                  {selected?.name || 'Unnamed'}
                </PopoverInput>
              </div>
              <div className="right">
                <Select size="sm" options={modelSelectOptions} value={selected?.model === null ? 'No Matching Record Type' : getValue(modelSelectOptions, selected?.model)} onChange={handleModelChange} nullable floatingSelectV2Props={{ nullableLabel: 'No Matching Record Type' }} />
                <Button className="ghost destructive" icon={DeleteIcon} title="Delete" onClick={handleDelete} />
              </div>
            </div>
            <MockDataTypeEntries entries={selected?.entries} mockTypeId={selected?.id} />
          </>
        )}
      </div>
    </ModalV2>
  );
};

export default MockDataTypesModal;
